import React, {useRef} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import {IntNodeComponent, InputComponent} from './Components';
import {Chatbot} from '../../states/actions';
import {createSelectNodeData} from '../../states/selectors/chatbotSelectors';

export const GTMBlock = React.memo((node) => {
  const {block, start} = useSelector(createSelectNodeData(node.id), shallowEqual);

  if (!block) {
    return null;
  }

  const {data} = block;

  return (
    <IntNodeComponent
      name="Google Tag Manager"
      icon="gtm"
      data={data}
      node={node}
      start={start}
    />
  );
});

const GtmPropertyRow = ({
  property,
  onUpdate,
  onRemove,
  onToggleHash,
}) => (
  <div key={property.id} className="double-input mb-3">
    <div className="inputs">
      <input
        name={`property.${property.id}.name`}
        className="form-control top"
        placeholder="property name"
        defaultValue={property.name}
        onBlur={(e) => onUpdate(property.id, {name: e.target.value})}
      />
      <input
        name={`property.${property.id}.value`}
        className="form-control bottom"
        placeholder="value or {variable_name}"
        defaultValue={property.value}
        onBlur={(e) => onUpdate(property.id, {value: e.target.value})}
      />
      <div className="px-2 py-1" title="SHA-256 hash value before push">
        <label className="small mb-0 d-flex align-items-center gap-1">
          <input
            type="checkbox"
            className="form-check-input mt-0"
            checked={property.hash === true}
            onChange={() => onToggleHash(property.id, !property.hash)}
            aria-label="SHA-256"
          />
          SHA-256
        </label>
      </div>
    </div>
    <span className="icon red" onClick={(e) => onRemove(e, property.id)} role="button">
      <i className="fa fa-trash-alt"></i>
    </span>
  </div>
);

const GtmPropertyAdder = ({onAdd, addId}) => {
  const refTxtName = useRef(null);
  const refTxtValue = useRef(null);

  const handleAdd = (e) => {
    e.preventDefault();
    const name = refTxtName.current?.value?.trim();
    const value = refTxtValue.current?.value?.trim() ?? '';
    if (!name) {
      return;
    }
    onAdd({name, value});
    if (refTxtName.current) refTxtName.current.value = '';
    if (refTxtValue.current) refTxtValue.current.value = '';
  };

  return (
    <div id={addId} className="double-input">
      <div className="inputs">
        <input
          type="text"
          className="form-control top"
          placeholder="property name"
          ref={refTxtName}
          onKeyPress={(e) => (e.key === 'Enter' ? handleAdd(e) : undefined)}
        />
        <input
          type="text"
          className="form-control bottom"
          placeholder="value or {variable_name}"
          ref={refTxtValue}
          onKeyPress={(e) => (e.key === 'Enter' ? handleAdd(e) : undefined)}
          onBlur={handleAdd}
        />
      </div>
      <span className="icon" onClick={handleAdd} role="button">
        <i className="fa fa-plus"></i>
      </span>
    </div>
  );
};

const GtmPropertySection = ({
  title,
  hint,
  addLabel,
  dataMap,
  onAdd,
  onUpdate,
  onRemove,
  onToggleHash,
  addId,
}) => (
  <>
    <div className="section mt-4">{title}</div>
    {hint && (
      <div className="mb-2">
        <small style={{fontStyle: 'italic', color: '#666'}}>{hint}</small>
      </div>
    )}
    {Object.keys(dataMap)
      .sort()
      .map((key) => {
        const property = dataMap[key];
        return (
          <GtmPropertyRow
            key={property.id}
            property={property}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onToggleHash={onToggleHash}
          />
        );
      })}
    <div className="mb-3">
      <label htmlFor={addId} className="form-label">
        {addLabel}
      </label>
      <GtmPropertyAdder onAdd={onAdd} addId={addId} />
    </div>
  </>
);

const createPropertySectionHandlers = (block, dataMap, fieldName) => {
  const addProperty = ({name, value}) => {
    const id = Date.now().toString();
    Chatbot.setBlockData(block.id, {[fieldName]: {[id]: {id, name, value, hash: false}}});
  };

  const updateProperty = (id, updates) => {
    const current = dataMap[id] || {};
    Chatbot.setBlockData(block.id, {
      [fieldName]: {
        [id]: {
          id,
          name: updates.name !== undefined ? updates.name : current.name,
          value: updates.value !== undefined ? updates.value : current.value,
          hash: updates.hash !== undefined ? updates.hash : current.hash === true,
        },
      },
    });
  };

  const toggleHash = (id, hash) => {
    updateProperty(id, {hash});
  };

  return {addProperty, updateProperty, toggleHash};
};

const getOrCreateItemId = (items) => {
  const keys = Object.keys(items || {}).sort();
  return keys.length > 0 ? keys[0] : null;
};

export const GTMEditor = ({nodeid, block, data}) => {
  const variables = data.variables || {};
  const items = data.items || {};
  const userData = data.user_data || {};
  const itemId = getOrCreateItemId(items);
  const itemProperties = itemId ? items[itemId]?.properties || {} : {};

  const variableHandlers = createPropertySectionHandlers(block, variables, 'variables');
  const userDataHandlers = createPropertySectionHandlers(block, userData, 'user_data');

  const removeVariableField = (e, id) => {
    e.preventDefault();
    Chatbot.removeBlockVariable(nodeid, id);
  };

  const removeUserDataField = (e, id) => {
    e.preventDefault();
    Chatbot.removeGtmUserDataProperty(nodeid, id);
  };

  const addItemProperty = ({name, value}) => {
    const base = Date.now();
    const targetItemId = itemId || String(base);
    const propId = String(base + 1);
    const item = itemId ? items[itemId] : {id: targetItemId, properties: {}};
    Chatbot.setBlockData(block.id, {
      items: {
        [targetItemId]: {
          ...item,
          id: targetItemId,
          properties: {
            ...(item.properties || {}),
            [propId]: {id: propId, name, value, hash: false},
          },
        },
      },
    });
  };

  const updateItemProperty = (propId, updates) => {
    if (!itemId) {
      return;
    }
    const item = items[itemId] || {id: itemId, properties: {}};
    const current = item.properties?.[propId] || {};
    Chatbot.setBlockData(block.id, {
      items: {
        [itemId]: {
          ...item,
          properties: {
            [propId]: {
              id: propId,
              name: updates.name !== undefined ? updates.name : current.name,
              value: updates.value !== undefined ? updates.value : current.value,
              hash: updates.hash !== undefined ? updates.hash : current.hash === true,
            },
          },
        },
      },
    });
  };

  const toggleItemPropertyHash = (propId, hash) => {
    updateItemProperty(propId, {hash});
  };

  const removeItemProperty = (e, propId) => {
    e.preventDefault();
    if (!itemId) {
      return;
    }
    Chatbot.removeGtmItemProperty(nodeid, itemId, propId);
  };

  const handleBlur = (e) => {
    Chatbot.setBlockData(block.id, {[e.target.name]: e.target.value});
  };

  return (
    <>
      <div className="section">GOOGLE TAG MANAGER</div>

      <InputComponent
        data={data}
        name="Event name"
        dataname="event"
        placeholder="registration_started"
        onBlur={handleBlur}
      />

      <div className="mb-2">
        <small style={{fontStyle: 'italic', color: '#666'}}>
          Use {'{variable_name}'} in value fields to insert chat variables. SHA-256 normalizes before
          hashing: email is trimmed and lowercased, phone keeps digits only.
        </small>
      </div>

      <GtmPropertySection
        title="TOP-LEVEL DATALAYER PROPERTIES"
        hint="Pushed as top-level dataLayer keys (e.g. transaction_id, value, currency, or custom properties)."
        addLabel="Add top-level property (optional)"
        dataMap={variables}
        onAdd={variableHandlers.addProperty}
        onUpdate={variableHandlers.updateProperty}
        onRemove={removeVariableField}
        onToggleHash={variableHandlers.toggleHash}
        addId="gtm-variables-new"
      />

      <GtmPropertySection
        title="ITEMS"
        hint="Item fields such as item_id, item_name, price, quantity. Pushed as top-level items object in the dataLayer."
        addLabel="Add item property (optional)"
        dataMap={itemProperties}
        onAdd={addItemProperty}
        onUpdate={updateItemProperty}
        onRemove={removeItemProperty}
        onToggleHash={toggleItemPropertyHash}
        addId="gtm-items-new"
      />

      <GtmPropertySection
        title="USER DATA"
        hint="Properties such as email for Enhanced Conversions. Enable SHA-256 for hashed PII."
        addLabel="Add user_data property (optional)"
        dataMap={userData}
        onAdd={userDataHandlers.addProperty}
        onUpdate={userDataHandlers.updateProperty}
        onRemove={removeUserDataField}
        onToggleHash={userDataHandlers.toggleHash}
        addId="gtm-user-data-new"
      />
    </>
  );
};
